import React, { useEffect, useState } from "react";
import axios from "axios";
import config from "../config/config";
import { CircleUserRound, FileUser, Newspaper, ShieldCheck, UserCog, Wrench } from "lucide-react";
import CockpitShell from "../components/CockpitShell";
import ApplicantShell from "../components/ApplicantShell";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import AppearanceToggle from "../components/AppearanceToggle";
import { useAppearance } from "../context/AppearanceContext";
import { normalizeRole } from "../utils/roleMap";
import { clearStoredAuth, decodeJwtPayload, isJwtExpired } from "../utils/authSession";

const navByRole = {
  applicant: [
    { href: "/dashboard/applicant", label: "Dashboard", icon: UserCog },
    { href: "/dashboard/account", label: "Konto", icon: ShieldCheck },
    { href: "/dashboard/applicant/bewerbung", label: "Bewerbung", icon: FileUser },
  ],
  employer: [
    { href: "/dashboard/employer", label: "Uebersicht", icon: UserCog },
    { href: "/dashboard/account", label: "Konto", icon: ShieldCheck },
    { href: "/dashboard/stb/anzeigen", label: "Anzeigen", icon: FileUser },
  ],
  press: [
    { href: "/dashboard/press", label: "Pressebereich", icon: Newspaper },
    { href: "/dashboard/account", label: "Konto", icon: ShieldCheck },
  ],
  admin: [
    { href: "/dashboard/admin", label: "Admin-Uebersicht", icon: Wrench },
    { href: "/dashboard/account", label: "Konto", icon: ShieldCheck },
  ],
  support: [
    { href: "/dashboard/admin", label: "Support-Uebersicht", icon: Wrench },
    { href: "/dashboard/account", label: "Konto", icon: ShieldCheck },
  ],
  editor: [
    { href: "/dashboard/admin", label: "Redaktion", icon: CircleUserRound },
    { href: "/dashboard/account", label: "Konto", icon: ShieldCheck },
  ],
};

function authHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function readIdentityFromStorage() {
  const token = localStorage.getItem("token");
  if (!token || isJwtExpired(token)) {
    clearStoredAuth();
    return {
      userid: "-",
      email: "-",
      role: "applicant",
      is_active: null,
    };
  }

  const payload = decodeJwtPayload(token);
  return {
    userid: localStorage.getItem("userid") || payload?.userid || "-",
    email: payload?.email || "-",
    role: normalizeRole(localStorage.getItem("role") || payload?.role || "applicant") || "applicant",
    is_active: payload ? 1 : null,
  };
}

const AccountSettings = () => {
  const { resolvedMode } = useAppearance();
  const [me, setMe] = useState(readIdentityFromStorage());
  const [sessions, setSessions] = useState([]);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [message, setMessage] = useState("");
  const [sessionIdleTimeoutMinutes, setSessionIdleTimeoutMinutes] = useState(120);
  const [sessionSettingsSaved, setSessionSettingsSaved] = useState("");
  const [sessionsMessage, setSessionsMessage] = useState("");
  const [settingsMessage, setSettingsMessage] = useState("");

  const role = normalizeRole(localStorage.getItem("role")) || "applicant";
  const navItems = navByRole[role] || navByRole.applicant;

  const load = async () => {
    const fallbackIdentity = readIdentityFromStorage();
    setMessage("");
    setSessionsMessage("");
    setSettingsMessage("");

    const [meResult, sessionsResult, settingsResult] = await Promise.allSettled([
      axios.get(`${config.API_BASE_URL}/api/me`, { headers: authHeaders() }),
      axios.get(`${config.API_BASE_URL}/api/me/sessions`, { headers: authHeaders() }),
      axios.get(`${config.API_BASE_URL}/api/me/session-settings`, { headers: authHeaders() }),
    ]);

    if (meResult.status === "fulfilled") {
      setMe({ ...fallbackIdentity, ...(meResult.value.data || {}) });
    } else {
      setMe(fallbackIdentity);
      if (meResult.reason?.response?.status === 401) {
        clearStoredAuth();
        window.location.assign("/login");
        return;
      }
      setMessage("Die vollstaendigen Kontodaten konnten nicht geladen werden. Grunddaten aus deiner aktuellen Anmeldung werden weiter angezeigt.");
    }

    if (sessionsResult.status === "fulfilled") {
      setSessions(sessionsResult.value.data?.sessions || []);
    } else {
      setSessions([]);
      setSessionsMessage("Die aktiven Sitzungen konnten im laufenden Backend gerade nicht gelesen werden.");
    }

    if (settingsResult.status === "fulfilled") {
      setSessionIdleTimeoutMinutes(settingsResult.value.data?.session_idle_timeout_minutes || 120);
    } else {
      setSessionIdleTimeoutMinutes(fallbackIdentity?.session_idle_timeout_minutes || 120);
      setSettingsMessage("Die gespeicherte Inaktivitaetszeit konnte nicht vom Backend gelesen werden. Der letzte bekannte Wert bleibt sichtbar.");
    }
  };

  useEffect(() => {
    document.title = "Experts4Europe | Kontoeinstellungen";
    load();
  }, []);

  const shellProps =
    role === "applicant"
      ? {
          component: ApplicantShell,
          props: {
            title: "Konto",
            subtitle:
              "Appearance, Sicherheit und Sitzungen liegen in derselben light-first Kontostruktur wie der restliche Bewerber-Arbeitsplatz.",
          },
        }
      : {
          component: CockpitShell,
          props: {
            title: "Kontoeinstellungen",
            subtitle: "Appearance, Sicherheit und Sitzungssteuerung fuer dein Konto in derselben Master-Shell.",
            badge: "Experts4Europe Account",
            navItems,
            theme: role === "employer" ? "employer" : "default",
            shellLabel: "Account Center",
            shellDescription: "Appearance, Sicherheit und Sessions bleiben kompakt in derselben Template-Sprache.",
            modeLabel: "Account Settings",
            liveLabel: role,
          },
        };

  const ShellComponent = shellProps.component;

  return (
    <ShellComponent {...shellProps.props}>
      {message ? <p className="rounded-lg border border-rose-400/30 bg-rose-500/10 p-3 text-sm text-rose-200">{message}</p> : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="dashboard-card-shell">
          <CardHeader><CardTitle className="dashboard-card-title">Konto ueberblick</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm dashboard-card-copy">
            <p>UserID: <strong>{me?.userid || "-"}</strong></p>
            <p>E-Mail: <strong>{me?.email || "-"}</strong></p>
            <p>Rolle: <strong>{me?.role || "-"}</strong></p>
            <p>Status: <strong>{me?.is_active === 0 ? "inaktiv" : me?.is_active === 1 ? "aktiv" : "noch nicht geladen"}</strong></p>
          </CardContent>
        </Card>

        <Card className="dashboard-card-shell">
          <CardHeader><CardTitle className="dashboard-card-title">Appearance</CardTitle></CardHeader>
          <CardContent className="space-y-4 dashboard-card-copy">
            <p>Tag-Modus bleibt Standard. Nacht-Modus ist dieselbe Struktur mit anderen Tokens und kann hier dauerhaft gewaehlt werden.</p>
            <AppearanceToggle />
            <p className="text-sm text-slate-500">Aktiv wirksam: <strong>{resolvedMode === "night" ? "Nacht" : "Tag"}</strong></p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="dashboard-card-shell">
          <CardHeader><CardTitle className="dashboard-card-title">Passwort aendern</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <Input type="password" placeholder="Aktuelles Passwort" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} className="dashboard-field" />
            <Input type="password" placeholder="Neues Passwort (mindestens 8 Zeichen)" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="dashboard-field" />
            <Button className="dashboard-action-button" onClick={async () => {
              try {
                await axios.post(`${config.API_BASE_URL}/api/me/change-password`, { old_password: oldPassword, new_password: newPassword }, { headers: authHeaders() });
                setMessage("Passwort erfolgreich aktualisiert.");
                setOldPassword("");
                setNewPassword("");
              } catch (e) {
                setMessage(e.response?.data?.message || "Das Passwort konnte nicht aktualisiert werden.");
              }
            }}>
              Passwort aktualisieren
            </Button>
          </CardContent>
        </Card>

        <Card className="dashboard-card-shell">
          <CardHeader>
            <CardTitle className="dashboard-card-title">Sitzungen automatisch beenden</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px_auto] lg:items-end dashboard-card-copy">
            <div className="space-y-2 text-sm">
              <p>
                Lege fest, nach wie vielen Minuten Inaktivitaet deine Sitzung automatisch endet.
                Beim naechsten Login werden aeltere aktive Sitzungen ausserdem automatisch beendet.
              </p>
              <p className="text-slate-500">
                Das gilt fuer dein Konto rollenuebergreifend und sorgt dafuer, dass alte Browser- oder Test-Sitzungen nicht unbegrenzt offen bleiben.
              </p>
              {settingsMessage ? <p className="text-xs text-amber-600">{settingsMessage}</p> : null}
            </div>
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">
                Idle-Zeit in Minuten
              </label>
              <Input
                type="number"
                min="5"
                max="10080"
                value={sessionIdleTimeoutMinutes}
                onChange={(e) => setSessionIdleTimeoutMinutes(e.target.value)}
                className="dashboard-field"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Button
                className="dashboard-action-button"
                onClick={async () => {
                  try {
                    const response = await axios.patch(
                      `${config.API_BASE_URL}/api/me/session-settings`,
                      { session_idle_timeout_minutes: sessionIdleTimeoutMinutes },
                      { headers: authHeaders() }
                    );
                    setSessionIdleTimeoutMinutes(response.data?.settings?.session_idle_timeout_minutes || sessionIdleTimeoutMinutes);
                    setSessionSettingsSaved("Inaktivitaetszeit gespeichert.");
                    setMessage("");
                    await load();
                  } catch (e) {
                    setSessionSettingsSaved("");
                    setMessage(e.response?.data?.message || "Die Sitzungszeit konnte nicht gespeichert werden.");
                  }
                }}
              >
                Inaktivitaetszeit speichern
              </Button>
              {sessionSettingsSaved ? (
                <p className="text-xs text-emerald-600">{sessionSettingsSaved}</p>
              ) : null}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="dashboard-card-shell">
        <CardHeader><CardTitle className="dashboard-card-title">Aktive Sitzungen</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <p className="text-sm text-slate-500">
            Angezeigt werden nur noch aktuell gueltige Sitzungen. Abgelaufene oder bereits beendete Sitzungen verschwinden automatisch aus dieser Liste.
          </p>
          {sessionsMessage ? <p className="text-xs text-amber-600">{sessionsMessage}</p> : null}
          {sessions.length ? sessions.map((s) => (
            <div key={s.id} className="dashboard-session-row">
              <div>
                <p>Sitzung #{s.id}</p>
                <p className="text-slate-500">{s.ip || "-"} · {s.user_agent || "-"}</p>
                <p className="text-xs text-slate-400">
                  Letzte Aktivitaet: {s.last_seen_at ? new Date(s.last_seen_at).toLocaleString("de-DE") : "-"}
                </p>
              </div>
              <Button size="sm" variant="outline" className="dashboard-action-button dashboard-action-button-danger" onClick={async () => {
                await axios.delete(`${config.API_BASE_URL}/api/me/sessions/${s.id}`, { headers: authHeaders() });
                await load();
              }}>
                Beenden
              </Button>
            </div>
          )) : (
            <div className="dashboard-empty-tile">
              Aktuell ist keine weitere gueltige Sitzung offen.
            </div>
          )}
        </CardContent>
      </Card>
    </ShellComponent>
  );
};

export default AccountSettings;
